import { useTranslation } from 'react-i18next';
import { ChevronLeft, Icon } from '../ui/Icon';
import { GiaLogoGlow, GiaName } from './GiaLogo';
import AiChatPanel from './AiChatPanel';
import { useAiPanelState } from './AiPanelContext';
import { useAiActionDispatch } from './useAiActionDispatch';
import { useAuthStore } from '../../stores/auth.store';

import type { Trip } from '../../types';

interface Props {
  trip: Trip;
}

// Credit-badge colour mirrors the header badge inside AiChatPanel.
function badgeClasses(out: boolean, low: boolean, premium: boolean): string {
  if (out) return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
  if (low) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
  if (premium) return 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400';
  return 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400';
}

/**
 * The assistant docked to the right of the trip layout. On desktop it lives in
 * the flex row as a slim rail that expands into a full panel (pushing content);
 * on small screens the rail becomes a floating pill and the panel a slide-over
 * drawer. Rendered once at the layout level — the active tab sets the context
 * via `useAiPanel`.
 *
 * `AiChatPanel` is kept mounted at all times (its visibility is toggled with
 * CSS, never conditional rendering) so the conversation survives collapsing and
 * moving between tabs. When the current tab has no assistant the whole dock is
 * hidden but the panel stays alive in the DOM.
 */
export default function AiDockPanel({ trip }: Props) {
  const { t } = useTranslation();
  const tripId = trip.id;
  const destination = trip.destination;
  const { context, lastContext, collapsed, setCollapsed } = useAiPanelState();
  const dispatchAction = useAiActionDispatch();
  const { user } = useAuthStore();

  // Keep a stable context (and thus the conversation) while the user visits a
  // tab that offers no assistant.
  const effectiveContext = context ?? lastContext;

  // Nothing to show until an assistant-enabled tab has been visited at least once.
  if (!effectiveContext) return null;

  const active = context !== null; // current tab offers the assistant
  const showRail = active && collapsed;
  const showPanel = active && !collapsed;

  const isAdmin = user?.isAdmin ?? false;
  const premiumCredits = user?.premiumAiCredits ?? 0;
  const freeCredits = user?.aiCredits ?? 0;
  const isPro = user?.isPro ?? false;
  const isPremium = isAdmin || isPro || premiumCredits > 0;
  const activeCredits = isPremium ? premiumCredits : freeCredits;
  const outOfCredits = !isAdmin && !isPro && premiumCredits <= 0 && freeCredits <= 0;

  const expand = () => setCollapsed(false);
  const collapse = () => setCollapsed(true);

  return (
    <>
      {/* ── Rail (desktop, collapsed) ─────────────────────────────── */}
      {showRail && (
        <aside className="relative hidden md:flex flex-col items-center shrink-0 w-14 h-full bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800">
          <button
            onClick={expand}
            className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center text-slate-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors cursor-pointer"
            aria-label={t('ai.expand')}
          >
            <Icon as={ChevronLeft} size={14} />
          </button>

          <button
            onClick={expand}
            data-tour="itinerary-ai"
            className="mt-4 rounded-full shadow-sm cursor-pointer"
            aria-label={t('ai.expand')}
            title={t('ai.name')}
          >
            <GiaLogoGlow size={36} />
          </button>

          {!isAdmin && (
            <span className={`mt-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${badgeClasses(outOfCredits, activeCredits <= 5, isPremium)}`}>
              {isPro ? 'PRO' : activeCredits}
            </span>
          )}

          <div className="flex-1 w-px my-4 bg-slate-200 dark:bg-slate-800" />

          <div className="pb-6 flex flex-col items-center gap-2 [writing-mode:vertical-rl] text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 dark:text-slate-600 select-none">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
            <span className="truncate max-h-[45vh]"><GiaName /> · {destination}</span>
          </div>
        </aside>
      )}

      {/* ── Mobile drawer backdrop ────────────────────────────────── */}
      {showPanel && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-slate-900/40 animate-fade-in"
          onClick={collapse}
          aria-hidden
        />
      )}

      {/* ── Panel (always mounted; visibility toggled) ────────────── */}
      <aside
        className={`${showPanel ? 'flex' : 'hidden'} fixed inset-y-0 right-0 z-50 w-full max-w-md flex-col border-l border-slate-200 dark:border-slate-800 shadow-2xl md:static md:z-auto md:inset-auto md:w-96 md:max-w-none md:h-full md:shadow-none`}
      >
        <AiChatPanel
          tripId={tripId}
          trip={trip}
          context={effectiveContext}
          onCollapse={collapse}
          onAction={dispatchAction}
        />
      </aside>

      {/* ── Pill (mobile, collapsed) ──────────────────────────────── */}
      {showRail && (
        <button
          onClick={expand}
          data-tour="itinerary-ai"
          className="md:hidden fixed bottom-20 right-4 z-40 rounded-full shadow-lg hover:brightness-110 transition-all cursor-pointer"
          aria-label={t('ai.expand')}
        >
          <GiaLogoGlow size={56} />
        </button>
      )}
    </>
  );
}
